import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { ACTIVE, isCorrectNetwork, parseChainId } from '../config/networks';
import { getDefaultProvider, detectWallets } from '../utils/wallet';
import { switchOrAddNetwork } from '../utils/network';
import { useNotify } from './NotificationContext';
import { parseError } from '../utils/errors';

const Web3Context = createContext();

export const Web3Provider = ({ children }) => {
  const notify = useNotify();

  const [account, setAccount]         = useState(null);
  const [chainId, setChainId]         = useState(null);
  const [provider, setProvider]       = useState(null);      // ethers provider
  const [signer, setSigner]           = useState(null);      // ethers signer
  const [rawProvider, setRawProvider] = useState(null);      // EIP-1193
  const [connecting, setConnecting]   = useState(false);
  const [switching, setSwitching]     = useState(false);
  const [walletName, setWalletName]   = useState(null);

  const rawProviderRef        = useRef(null);
  const switchInFlightRef     = useRef(false);     // block concurrent switch attempts
  const lastWarnedChainRef    = useRef(null);      // dedup "wrong network" warnings
  const lastWelcomedChainRef  = useRef(null);      // dedup "connected" success

  const rightNetwork = isCorrectNetwork(chainId);

  // Auto-attempt silent reconnect on mount
  useEffect(() => {
    const autoConnect = async () => {
      const eth = getDefaultProvider();
      if (!eth) return;
      try {
        const accts = await eth.request({ method: 'eth_accounts' });
        if (accts && accts.length > 0) {
          await _finishConnect(eth, accts[0]);
        }
      } catch (e) {
        // silent
      }
    };
    autoConnect();
  }, []);

  // Event listeners on raw provider
  useEffect(() => {
    const raw = rawProvider;
    if (!raw || !raw.on) return;

    const handleAccounts = (accts) => {
      if (!accts || accts.length === 0) {
        disconnect();
        notify.info('Wallet disconnected');
      } else {
        setAccount(accts[0]);
        _rebuildSigner(raw);
      }
    };

    const handleChain = (cidRaw) => {
      const cid = parseChainId(cidRaw);
      if (cid == null) return;
      setChainId(cid);
      _rebuildSigner(raw);
      _reactToChain(raw, cid);
    };

    const handleDisconnect = () => {
      disconnect();
    };

    raw.on('accountsChanged', handleAccounts);
    raw.on('chainChanged', handleChain);
    raw.on('disconnect', handleDisconnect);

    return () => {
      if (raw.removeListener) {
        raw.removeListener('accountsChanged', handleAccounts);
        raw.removeListener('chainChanged', handleChain);
        raw.removeListener('disconnect', handleDisconnect);
      }
    };
  }, [rawProvider]);

  const _rebuildSigner = (raw) => {
    try {
      const web3Provider = new ethers.providers.Web3Provider(raw, 'any');
      setProvider(web3Provider);
      setSigner(web3Provider.getSigner());
    } catch (e) {
      // ignore
    }
  };

  // Central handler for chain state changes. Runs after connect + on every chainChanged.
  // Deduped by tracking the last-warned / last-welcomed chainId in refs.
  const _reactToChain = (raw, cid) => {
    if (isCorrectNetwork(cid)) {
      // Right chain — welcome once (per unique correct-chain transition).
      lastWarnedChainRef.current = null;
      if (lastWelcomedChainRef.current !== cid) {
        lastWelcomedChainRef.current = cid;
        notify.success('Connected to ' + ACTIVE.displayName);
      }
      return;
    }

    // Wrong chain — warn once per distinct wrong chainId, and attempt one switch.
    lastWelcomedChainRef.current = null;
    if (lastWarnedChainRef.current !== cid) {
      lastWarnedChainRef.current = cid;
      notify.warning('Please switch to ' + ACTIVE.displayName + ' network');
    }
    if (!switchInFlightRef.current) {
      _attemptSwitch(raw);
    }
  };

  const _finishConnect = async (raw, address) => {
    rawProviderRef.current = raw;
    setRawProvider(raw);
    setAccount(address);

    // Identify wallet
    const wallets = detectWallets();
    const w = wallets.find((x) => x.provider === raw);
    setWalletName(w ? w.name : 'Wallet');

    const web3Provider = new ethers.providers.Web3Provider(raw, 'any');
    setProvider(web3Provider);
    setSigner(web3Provider.getSigner());

    // Get chainId + react (dedup + auto-switch)
    try {
      const cidRaw = await raw.request({ method: 'eth_chainId' });
      const cid = parseChainId(cidRaw);
      if (cid != null) {
        setChainId(cid);
        _reactToChain(raw, cid);
      }
    } catch (e) {
      // ignore
    }
  };

  const _attemptSwitch = async (raw) => {
    if (switchInFlightRef.current) return;
    switchInFlightRef.current = true;
    setSwitching(true);
    try {
      await switchOrAddNetwork(raw);
      // Don't notify here — chainChanged fires next and _reactToChain shows the success.
    } catch (e) {
      // Only surface real errors (not user-rejected which is common while switching)
      const code = e && e.code;
      if (code !== 4001 && code !== 'ACTION_REJECTED') {
        notify.error('Network switch failed: ' + parseError(e));
      }
    } finally {
      setSwitching(false);
      switchInFlightRef.current = false;
    }
  };

  const connect = async (targetProvider) => {
    setConnecting(true);
    try {
      const raw = targetProvider || getDefaultProvider();
      if (!raw) {
        notify.error('No wallet found. Please install Trust Wallet, MetaMask, or another Web3 wallet.');
        return false;
      }
      const accts = await raw.request({ method: 'eth_requestAccounts' });
      if (!accts || accts.length === 0) {
        notify.error('No accounts returned by wallet');
        return false;
      }
      await _finishConnect(raw, accts[0]);
      return true;
    } catch (e) {
      notify.error(parseError(e));
      return false;
    } finally {
      setConnecting(false);
    }
  };

  const switchNetwork = async () => {
    const raw = rawProviderRef.current || rawProvider;
    if (!raw) {
      notify.error('Connect wallet first');
      return;
    }
    // Force-clear dedup so user's explicit click can retry.
    switchInFlightRef.current = false;
    await _attemptSwitch(raw);
  };

  const disconnect = () => {
    setAccount(null);
    setChainId(null);
    setProvider(null);
    setSigner(null);
    setRawProvider(null);
    setWalletName(null);
    rawProviderRef.current = null;
    lastWarnedChainRef.current = null;
    lastWelcomedChainRef.current = null;
    switchInFlightRef.current = false;
  };

  const value = {
    account,
    chainId,
    provider,
    signer,
    rawProvider,
    walletName,
    connecting,
    switching,
    rightNetwork,
    isConnected: !!account,
    activeNetwork: ACTIVE,
    connect,
    disconnect,
    switchNetwork
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};

export const useWeb3 = () => {
  const ctx = useContext(Web3Context);
  if (!ctx) throw new Error('useWeb3 must be inside Web3Provider');
  return ctx;
};
